import React, { useState } from 'react';
import { Plus, Search, Filter, Calendar, Check, ChevronDown, SlidersHorizontal, ArrowDownUp } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { TaskRow } from '../components/task/TaskRow';
import { mockTasks, mockCompletedTasks } from '../data/mockData';
export const MyTasks = () => {
  const [tasks, setTasks] = useState(mockTasks);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortOption, setSortOption] = useState('newest');
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const handleCompleteTask = (taskId: string) => {
    const taskToComplete = tasks.find(task => task.id === taskId);
    if (taskToComplete) {
      const updatedTask = {
        ...taskToComplete,
        completedAt: new Date().toISOString()
      };
      // Remove from current tasks
      setTasks(tasks.filter(task => task.id !== taskId));
      // In a real app, you would update this in your backend
      console.log('Task completed:', updatedTask);
    }
  };
  // Filter and sort tasks
  const filteredTasks = tasks.filter(task => {
    // Search filter
    const matchesSearch = task.fileName.toLowerCase().includes(searchQuery.toLowerCase());
    // Status filter
    const matchesStatus = filterStatus === 'all' || filterStatus === 'completed' && task.completedAt || filterStatus === 'open' && !task.completedAt;
    // Priority filter
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  }).sort((a, b) => {
    // Sort logic
    switch (sortOption) {
      case 'newest':
        return new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime();
      case 'oldest':
        return new Date(a.postedAt).getTime() - new Date(b.postedAt).getTime();
      case 'priority-high':
        const priorityOrder = {
          high: 0,
          medium: 1,
          low: 2
        };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      case 'priority-low':
        const priorityOrderReverse = {
          low: 0,
          medium: 1,
          high: 2
        };
        return priorityOrderReverse[a.priority] - priorityOrderReverse[b.priority];
      case 'name-asc':
        return a.fileName.localeCompare(b.fileName);
      case 'name-desc':
        return b.fileName.localeCompare(a.fileName);
      default:
        return 0;
    }
  });
  return <div data-id="element-297">
      <PageHeader title="My Tasks" description="View and manage your assigned tasks" actions={<Button leftIcon={<Plus size={16} data-id="element-300" />} size="sm" className="sm:size-md" data-id="element-299">
            Add Task
          </Button>} data-id="element-298" />
      <div className="bg-surface-50 rounded-card border border-border shadow-subtle mb-6 overflow-hidden" data-id="element-301">
        {/* Filters and search */}
        <div className="p-4 border-b border-border" data-id="element-302">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-303">
            <div className="flex-1" data-id="element-304">
              <Input placeholder="Search by file name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<Search className="h-4 w-4 text-text-tertiary" data-id="element-306" />} data-id="element-305" />
            </div>
            <div className="flex space-x-2" data-id="element-307">
              {/* Filter dropdown */}
              <div className="relative" data-id="element-308">
                <Button variant="tertiary" leftIcon={<SlidersHorizontal size={16} data-id="element-310" />} rightIcon={<ChevronDown size={14} className={isSortMenuOpen ? 'rotate-180 transition-transform' : 'transition-transform'} data-id="element-311" />} onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)} className="w-full sm:w-auto" data-id="element-309">
                  Filter
                </Button>
                {isFilterMenuOpen && <div className="absolute right-0 mt-1 w-56 rounded-lg bg-surface-50 shadow-medium border border-border z-10" data-id="element-312">
                    <div className="p-3" data-id="element-313">
                      <div className="mb-3" data-id="element-314">
                        <label className="block text-xs font-medium text-text-secondary mb-1.5" data-id="element-315">
                          Status
                        </label>
                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full rounded-input border border-border bg-surface-100 px-3 py-1.5 text-sm" data-id="element-316">
                          <option value="all" data-id="element-317">All</option>
                          <option value="open" data-id="element-318">Open</option>
                          <option value="completed" data-id="element-319">Completed</option>
                        </select>
                      </div>
                      <div data-id="element-320">
                        <label className="block text-xs font-medium text-text-secondary mb-1.5" data-id="element-321">
                          Priority
                        </label>
                        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="w-full rounded-input border border-border bg-surface-100 px-3 py-1.5 text-sm" data-id="element-322">
                          <option value="all" data-id="element-323">All</option>
                          <option value="high" data-id="element-324">High</option>
                          <option value="medium" data-id="element-325">Medium</option>
                          <option value="low" data-id="element-326">Low</option>
                        </select>
                      </div>
                      <div className="mt-4 flex justify-end" data-id="element-327">
                        <Button size="sm" onClick={() => {
                      setFilterStatus('all');
                      setFilterPriority('all');
                      setIsFilterMenuOpen(false);
                    }} variant="tertiary" className="mr-2" data-id="element-328">
                          Reset
                        </Button>
                        <Button size="sm" onClick={() => setIsFilterMenuOpen(false)} data-id="element-329">
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>
              {/* Sort dropdown */}
              <div className="relative" data-id="element-330">
                <Button variant="tertiary" leftIcon={<ArrowDownUp size={16} data-id="element-332" />} rightIcon={<ChevronDown size={14} className={isSortMenuOpen ? 'rotate-180 transition-transform' : 'transition-transform'} data-id="element-333" />} onClick={() => setIsSortMenuOpen(!isSortMenuOpen)} className="w-full sm:w-auto" data-id="element-331">
                  Sort
                </Button>
                {isSortMenuOpen && <div className="absolute right-0 mt-1 w-48 rounded-lg bg-surface-50 shadow-medium border border-border z-10" data-id="element-334">
                    <div className="py-1" data-id="element-335">
                      <button className={`flex w-full items-center px-4 py-2 text-sm ${sortOption === 'newest' ? 'bg-brand-50 text-brand-700' : 'text-text-primary hover:bg-surface-100'}`} onClick={() => {
                    setSortOption('newest');
                    setIsSortMenuOpen(false);
                  }} data-id="element-336">
                        Newest first
                      </button>
                      <button className={`flex w-full items-center px-4 py-2 text-sm ${sortOption === 'oldest' ? 'bg-brand-50 text-brand-700' : 'text-text-primary hover:bg-surface-100'}`} onClick={() => {
                    setSortOption('oldest');
                    setIsSortMenuOpen(false);
                  }} data-id="element-337">
                        Oldest first
                      </button>
                      <button className={`flex w-full items-center px-4 py-2 text-sm ${sortOption === 'priority-high' ? 'bg-brand-50 text-brand-700' : 'text-text-primary hover:bg-surface-100'}`} onClick={() => {
                    setSortOption('priority-high');
                    setIsSortMenuOpen(false);
                  }} data-id="element-338">
                        Priority (high to low)
                      </button>
                      <button className={`flex w-full items-center px-4 py-2 text-sm ${sortOption === 'priority-low' ? 'bg-brand-50 text-brand-700' : 'text-text-primary hover:bg-surface-100'}`} onClick={() => {
                    setSortOption('priority-low');
                    setIsSortMenuOpen(false);
                  }} data-id="element-339">
                        Priority (low to high)
                      </button>
                      <button className={`flex w-full items-center px-4 py-2 text-sm ${sortOption === 'name-asc' ? 'bg-brand-50 text-brand-700' : 'text-text-primary hover:bg-surface-100'}`} onClick={() => {
                    setSortOption('name-asc');
                    setIsSortMenuOpen(false);
                  }} data-id="element-340">
                        File name (A-Z)
                      </button>
                      <button className={`flex w-full items-center px-4 py-2 text-sm ${sortOption === 'name-desc' ? 'bg-brand-50 text-brand-700' : 'text-text-primary hover:bg-surface-100'}`} onClick={() => {
                    setSortOption('name-desc');
                    setIsSortMenuOpen(false);
                  }} data-id="element-341">
                        File name (Z-A)
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
          {/* Active filters display */}
          {(filterStatus !== 'all' || filterPriority !== 'all') && <div className="flex flex-wrap gap-2 mt-3" data-id="element-342">
              {filterStatus !== 'all' && <div className="inline-flex items-center text-xs bg-surface-100 rounded-full px-2.5 py-1 text-text-secondary" data-id="element-343">
                  <span data-id="element-344">
                    Status: {filterStatus === 'open' ? 'Open' : 'Completed'}
                  </span>
                  <button className="ml-1.5 text-text-tertiary hover:text-text-primary" onClick={() => setFilterStatus('all')} data-id="element-345">
                    <X size={12} data-id="element-346" />
                  </button>
                </div>}
              {filterPriority !== 'all' && <div className="inline-flex items-center text-xs bg-surface-100 rounded-full px-2.5 py-1 text-text-secondary" data-id="element-347">
                  <span data-id="element-348">
                    Priority:{' '}
                    {filterPriority.charAt(0).toUpperCase() + filterPriority.slice(1)}
                  </span>
                  <button className="ml-1.5 text-text-tertiary hover:text-text-primary" onClick={() => setFilterPriority('all')} data-id="element-349">
                    <X size={12} data-id="element-350" />
                  </button>
                </div>}
            </div>}
        </div>
        {/* Task list */}
        <div data-id="element-351">
          {filteredTasks.length > 0 ? <div className="divide-y divide-border" data-id="element-352">
              {filteredTasks.map(task => <TaskRow key={task.id} task={task} onComplete={handleCompleteTask} data-id="element-353" />)}
            </div> : <div className="py-16 flex flex-col items-center justify-center px-4" data-id="element-354">
              <div className="bg-surface-100 p-3 rounded-full" data-id="element-355">
                <Check className="h-6 w-6 text-success-500" data-id="element-356" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-text-primary" data-id="element-357">
                All clear!
              </h3>
              <p className="mt-1 text-sm text-text-secondary text-center max-w-md" data-id="element-358">
                {searchQuery ? `No tasks matching "${searchQuery}"` : 'No open tasks at the moment.'}
              </p>
              <Button variant="primary" leftIcon={<Plus size={16} data-id="element-360" />} className="mt-4" data-id="element-359">
                Add Task
              </Button>
            </div>}
        </div>
      </div>
    </div>;
};